/**
 * Which outside scripts did this page actually get? One `script_health` event
 * per hard page load, read by the "Blocked scripts" tab on /dash/errors.
 *
 * Owner, 2026-10-28: an ad blocker can stop anything, Stripe and reCAPTCHA
 * included, and we had no numbers. Nothing in the page can read the browser's
 * reason (ERR_BLOCKED_BY_CLIENT is console-only), so this measures the effect:
 *
 *   failed     the <script> fired `error`: blocked, or the network dropped it.
 *   stub       it loaded but its global never appeared. uBlock Origin and Brave
 *              swap Google Analytics, AdSense and GTM for an empty stand-in.
 *   not_ready  no load or error seen and no global: blocked before this ran,
 *              or still loading when the report went (see `early`).
 *   ok         loaded, and its global is there where one proves it.
 *
 * Plus one bait element with ad-like class names. Every cosmetic filter list
 * (EasyList, AdGuard, Safari content blockers) hides it, so `blocker=1` says a
 * blocker is on even on a page where it had nothing to block.
 *
 * Our own endpoints get through for real blocked buyers: 3 of the 4 BotID
 * script failures on 2026-10-27..28 (iPhone, Android) still sent analytics.
 *
 * Probe v2 (2026-10-31, `v=2`): every script the page had is reported, under
 * its watched id or else its host, so a new third party shows up on its own
 * with ok and blocked counts. v1 rows named only failed unknown hosts
 * (`other_failed`). v2 also sends `brave`: '1' when `navigator.brave` exists.
 *
 * Probe v3 (2026-11-04, `v=3`): adds the robot proof (utils/robot-proof.ts),
 * `webdriver` and `ua_mismatch`. A browser with either at '1' is a robot in
 * every people number (utils/clickhouse/people-sql.ts). This event carries
 * them because it goes out on every hard load, scored or not, and costs no
 * reCAPTCHA call. A v3 row is also how the reader knows the page could have
 * reported a touch (utils/page-touch.ts).
 */
import { getAnalytics } from './analytics';
import { readRobotProof } from './robot-proof';
import { SCRIPT_HEALTH_EVENT, scriptIdFor, watchedScriptFor } from './script-health-vendors';

/** Long enough after `load` for async scripts to run, short enough to beat most exits. */
export const REPORT_AFTER_LOAD_MS = 8000;

/** Class names EasyList and AdGuard hide on every site. */
export const BAIT_CLASSES = 'adsbox ad-banner ad-placeholder pub_300x250 textads';

/** Which probe sent the row. 2: every script by id or host, plus `brave`. 3: robot proof. */
export const PROBE_VERSION = '3';

/** Scripts a page reports, at most; a page with more is not one we built. */
const MAX_SCRIPTS = 40;

export type ScriptStatus = 'ok' | 'failed' | 'stub' | 'not_ready';

const failedSrcs = new Set<string>();
const loadedSrcs = new Set<string>();
let bait: HTMLElement | null = null;
let installed = false;
let reported = false;

/** Test seam. */
export function resetScriptHealth(): void {
  failedSrcs.clear();
  loadedSrcs.clear();
  bait?.remove();
  bait = null;
  installed = false;
  reported = false;
}

function onResourceEvent(event: Event): void {
  const el = event.target;
  // Listened for only in a browser, where HTMLScriptElement always exists.
  if (!(el instanceof HTMLScriptElement) || !el.src) return;
  (event.type === 'error' ? failedSrcs : loadedSrcs).add(el.src);
}

/** Once per install, from the page's load: the body is there and no bait is yet. */
function plantBait(): void {
  bait = document.createElement('div');
  bait.className = BAIT_CLASSES;
  bait.setAttribute('aria-hidden', 'true');
  // A probe, not UI: it must take up space for a filter to visibly remove it,
  // and sit off screen so no one sees it.
  bait.style.cssText = 'position:absolute;left:-9999px;top:0;width:10px;height:10px;';
  bait.innerHTML = '&nbsp;';
  document.body.appendChild(bait);
}

/** '1' when a cosmetic filter hid the bait, '0' when it did not, '' when there was none. */
export function readBait(el: HTMLElement | null): '' | '0' | '1' {
  if (!el) return '';
  // Some blockers remove the node instead of hiding it.
  if (!el.isConnected) return '1';
  const style = window.getComputedStyle(el);
  const hidden = el.offsetHeight === 0 || style.display === 'none' || style.visibility === 'hidden';
  return hidden ? '1' : '0';
}

/** Script URLs the browser fetched, from resource timing: catches scripts that loaded and left the DOM. */
function timedScriptSrcs(): string[] {
  try {
    return performance
      .getEntriesByType('resource')
      .filter((e) => (e as PerformanceResourceTiming).initiatorType === 'script')
      .map((e) => e.name);
    // eslint-disable-next-line sellwild-code/catch-must-log -- an old browser without resource timing just has fewer sources
  } catch {
    return [];
  }
}

/** Every script URL the page holds, tried to load, or fetched. */
function scriptSrcs(): string[] {
  const inDom = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'), (s) => s.src);
  return [...new Set([...inDom, ...failedSrcs, ...timedScriptSrcs()])];
}

/** Whether the browser fetched this URL, for a script that loaded before the listener was on. */
function fetched(src: string): boolean {
  try {
    return performance.getEntriesByName(src).length > 0;
    // eslint-disable-next-line sellwild-code/catch-must-log -- no resource timing reads as not fetched
  } catch {
    return false;
  }
}

/**
 * Every script on the page, and what became of it: under its watched id, or
 * its host for one nobody named.
 */
export function readScriptStatuses(win: Window = window): Record<string, ScriptStatus> {
  const bySrc = new Map<string, string[]>();
  for (const src of scriptSrcs()) {
    const id = scriptIdFor(src);
    if (id) bySrc.set(id, [...(bySrc.get(id) ?? []), src]);
  }
  const statuses: Record<string, ScriptStatus> = {};
  for (const [id, srcs] of [...bySrc].slice(0, MAX_SCRIPTS)) {
    if (srcs.some((src) => failedSrcs.has(src))) {
      statuses[id] = 'failed';
      continue;
    }
    const arrived = srcs.some((src) => loadedSrcs.has(src) || fetched(src));
    const ready = watchedScriptFor(srcs[0])?.ready;
    if (ready) {
      statuses[id] = ready(win as unknown as Record<string, unknown>) ? 'ok' : arrived ? 'stub' : 'not_ready';
    } else {
      statuses[id] = arrived ? 'ok' : 'not_ready';
    }
  }
  return statuses;
}

/** '1' when the page runs in Brave, which says so on `navigator.brave`. */
export function readBrave(nav: Navigator = navigator): '0' | '1' {
  return (nav as Navigator & { brave?: unknown }).brave ? '1' : '0';
}

function idsWith(statuses: Record<string, ScriptStatus>, status: ScriptStatus): string {
  return Object.keys(statuses).filter((id) => statuses[id] === status).join(',');
}

/** The event's properties: every value a string, `properties` is a Map(String, String). */
export function buildScriptHealthProperties(early: boolean): Record<string, string> {
  const statuses = readScriptStatuses();
  return {
    blocker: readBait(bait),
    ok: idsWith(statuses, 'ok'),
    failed: idsWith(statuses, 'failed'),
    stub: idsWith(statuses, 'stub'),
    not_ready: idsWith(statuses, 'not_ready'),
    brave: readBrave(),
    ...readRobotProof(),
    v: PROBE_VERSION,
    early: early ? '1' : '0',
    ms: String(Math.max(0, Math.round(performance.now()))),
  };
}

function report(early: boolean): void {
  if (reported) return;
  reported = true;
  const analytics = getAnalytics();
  analytics?.track({
    event_type: SCRIPT_HEALTH_EVENT,
    properties: buildScriptHealthProperties(early),
  });
  // The tracker flushes its queue on `beforeunload`, which fires BEFORE
  // `pagehide`, so an early report used to sit in a queue nobody sent. The
  // fast leaver is the one this row matters most for (a driven browser gone
  // in two seconds), so the page going away sends it now, by beacon.
  if (early) void analytics?.flush(true);
  bait?.remove();
}

/**
 * Starts listening at module load in _app, so it sees the scripts that load
 * after it; the ones before it are read from the DOM and resource timing.
 */
export function installScriptHealth(): void {
  // pages/_app.tsx calls this only when there is a window.
  if (installed) return;
  installed = true;
  // Load and error do not bubble, and a load event never reaches window, so
  // both are caught on the way down at document.
  document.addEventListener('error', onResourceEvent, true);
  document.addEventListener('load', onResourceEvent, true);

  const schedule = () => {
    plantBait();
    setTimeout(() => report(false), REPORT_AFTER_LOAD_MS);
  };
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
  // A buyer who leaves fast is the one most likely to have hit a wall.
  window.addEventListener('pagehide', () => report(true), { once: true });
}
